import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { createOperationStudent } from '../../services/operationStudentService';
import { Save, Trash2, Search, Download } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTablePreferences } from '../../hooks/useTablePreferences';
import * as XLSX from 'xlsx';

interface EngineeringTableProps {
    programType: 'polito' | 'polimi' | 'all-engineering';
}

interface EngineeringStudent {
    id: string;
    first_name: string;
    last_name: string;
    universitaly_data: {
        username?: string;
        password?: string;
        university?: string;
        program?: string;
    };
    school_portal_data: {
        username?: string;
        password?: string;
        sat_score?: string;
    };
    exam_data: {
        exam1_date?: string;
        exam1_score?: string;
        exam2_date?: string;
        exam2_score?: string;
        exam3_date?: string;
        exam3_score?: string;
        exam_fee_status?: string;
        enrollment_fee_status?: string;
    };
    documents_status: {
        language_cert?: string;
        passport_status?: string;
        missing_documents?: string;
    };
    notes?: string;
    program_preferences?: string[];
}

const EngineeringTable: React.FC<EngineeringTableProps> = ({ programType }) => {

    const { user } = useAuth();
    const [students, setStudents] = useState<EngineeringStudent[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Add Student State
    const initialNewStudent = {
        first_name: '',
        last_name: '',
        university: 'Politecnico di Torino',
        program: '',
        universitaly_data: { username: '', password: '', program: '', university: '' },
        school_portal_data: { username: '', password: '', sat_score: '' },
        exam_data: {
            exam1_date: '', exam1_score: '',
            exam2_date: '', exam2_score: '',
            exam3_date: '', exam3_score: '',
            exam_fee_status: '', enrollment_fee_status: ''
        },
        documents_status: { language_cert: '', passport_status: '', missing_documents: '' },
        notes: ''
    };
    const [newStudent, setNewStudent] = useState(initialNewStudent);
    const [isAdding, setIsAdding] = useState(false);
    const [availableUniversities, setAvailableUniversities] = useState<{ id: string; name: string }[]>([]);

    // Column Preferences
    const defaultWidths = {
        lang_cert: 96,
        missing_docs: 96,
        first_name: 128,
        last_name: 128,
        university: 150,
        program: 150,
        uni_username: 100,
        uni_password: 100,
        portal_username: 100,
        portal_password: 100,
        sat_score: 70,
        lang_score: 96,
        passport: 96,
        exam1_date: 90,
        exam1_score: 70,
        exam2_date: 90,
        exam2_score: 70,
        exam3_date: 90,
        exam3_score: 70,
        fees: 110,
        notes: 200,
        actions: 80
    };

    const { columnWidths, setColumnWidths } = useTablePreferences('engineering_column_widths', defaultWidths);

    const handleMouseDown = (e: React.MouseEvent, key: string) => {
        const startX = e.clientX;
        const startWidth = columnWidths[key];

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const newWidth = Math.max(50, startWidth + (moveEvent.clientX - startX));
            setColumnWidths(prev => ({ ...prev, [key]: newWidth }));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const columnOrder = [
        'lang_cert', 'missing_docs', 'first_name', 'last_name', // Temel Bilgiler (4)
        'university', 'program', // Tercihler (2)
        'uni_username', 'uni_password', // Universitaly (2)
        'portal_username', 'portal_password', 'sat_score', // Okul Portalı (3)
        'lang_score', 'passport', // Diğer (2)
        'exam1_date', 'exam1_score', // 1. Sınav (2)
        'exam2_date', 'exam2_score', // 2. Sınav (2)
        'exam3_date', 'exam3_score', // 3. Sınav (2)
        'fees', 'notes', 'actions' // Diğer (3)
    ];

    useEffect(() => {
        const fetchUnis = async () => {
            const { data } = await supabase.from('universities').select('id, name').order('name');
            if (data) setAvailableUniversities(data);
        };
        fetchUnis();
    }, []);

    useEffect(() => {
        fetchStudents();
    }, [programType]);

    const fetchStudents = async () => {
        try {
            setLoading(true);

            let query = supabase
                .from('operation_students')
                .select('*')
                .order('created_at', { ascending: false });

            if (user?.branchId) query = query.eq('branch_id', user.branchId);
            const { data: opStudents, error } = await query;

            if (error) throw error;

            const filtered = (opStudents || []).filter(s => {
                const notesLower = (s.notes || '').toLowerCase();
                const progLower = (s.universitaly_data?.program || '').toLowerCase();

                if (programType === 'all-engineering') {
                    const keywords = ['polito', 'torino', 'polimi', 'milano', 'muhendislik', 'mühendislik', 'engineering'];
                    return keywords.some(k => notesLower.includes(k) || progLower.includes(k));
                }

                const k = programType === 'polito' ? ['polito', 'torino'] : ['polimi', 'milano'];
                return k.some(keyword => notesLower.includes(keyword) || progLower.includes(keyword));
            });

            setStudents(filtered.map(s => ({
                ...s,
                universitaly_data: s.universitaly_data || {},
                school_portal_data: s.school_portal_data || {},
                exam_data: s.exam_data || {},
                documents_status: s.documents_status || {},
                program_preferences: s.program_preferences || []
            })));
        } catch (error) {
            console.error('Error fetching students:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (student: EngineeringStudent) => {
        try {
            const { error } = await supabase
                .from('operation_students')
                .update({
                    universitaly_data: student.universitaly_data,
                    school_portal_data: student.school_portal_data,
                    exam_data: student.exam_data,
                    documents_status: student.documents_status,
                    notes: student.notes,
                    first_name: student.first_name,
                    last_name: student.last_name
                })
                .eq('id', student.id);

            if (error) throw error;
            alert('Kayıt güncellendi');
        } catch (error) {
            console.error('Error saving:', error);
            alert('Kaydedilirken hata oluştu');
        }
    };

    const handleDelete = async (id: string) => {
        if (!await window.appConfirm('Bu öğrenciyi silmek istediğinize emin misiniz?')) return;
        try {
            const { error } = await supabase
                .from('operation_students')
                .delete()
                .eq('id', id);

            if (error) throw error;
            setStudents(prev => prev.filter(s => s.id !== id));
        } catch (error) {
            console.error('Error deleting:', error);
            alert('Silinirken hata oluştu');
        }
    };

    const handleAddStudent = async () => {
        if (!newStudent.first_name || !newStudent.last_name) {
            alert('Lütfen ad ve soyad giriniz.');
            return;
        }

        try {
            setIsAdding(true);

            const uniName = newStudent.university;
            const programName = newStudent.program;
            let progType = uniName;
            if (uniName.includes('Torino')) progType = 'polito';
            else if (uniName.includes('Milano') && uniName.includes('Politecnico')) progType = 'polimi';

            const { data, error } = await createOperationStudent({
                p_first_name: newStudent.first_name,
                p_last_name: newStudent.last_name,
                p_branch_id: user?.branchId,
                p_program_type: progType,
                p_university_name: uniName,
                p_program_name: programName || 'Engineering',
                p_created_by: user?.id,
                p_is_eu: false,
                p_universitaly_data: {
                    ...newStudent.universitaly_data,
                    program: programName,
                    university: uniName
                },
                p_school_portal_data: newStudent.school_portal_data,
                p_exam_data: newStudent.exam_data,
                p_documents_status: newStudent.documents_status,
                p_notes: newStudent.notes
            });

            if (error) throw error;
            if (data && !data.success) throw new Error(data.error);

            await fetchStudents();
            setNewStudent(initialNewStudent);
            alert('Öğrenci başarıyla eklendi.');

        } catch (error: any) {
            console.error('Error adding student:', error);
            alert('Öğrenci eklenirken hata (Lütfen SQL kodunu Supabase üzerinde çalıştırmayı unutmayın): ' + error.message);
        } finally {
            setIsAdding(false);
        }
    };

    const updateStudentState = (id: string, field: string, value: any, nestedField?: string, subField?: string) => {
        setStudents(prev => prev.map(s => {
            if (s.id !== id) return s;
            const updated = { ...s };

            if (nestedField && subField) {
                // @ts-ignore
                updated[nestedField] = { ...updated[nestedField], [subField]: value };
            } else if (!nestedField) {
                // @ts-ignore
                updated[field] = value;
            }
            return updated as EngineeringStudent;
        }));
    };

    const exportToExcel = () => {
        const exportData = students.map(s => ({
            'Ad': s.first_name,
            'Soyad': s.last_name,
            'Üniversite': s.universitaly_data?.university,
            'Bölüm': s.universitaly_data?.program,
            'Universitaly Kullanıcı': s.universitaly_data?.username,
            'Universitaly Şifre': s.universitaly_data?.password,
            'Portal Kullanıcı': s.school_portal_data?.username,
            'Portal Şifre': s.school_portal_data?.password,
            'SAT': s.school_portal_data?.sat_score,
            'Dil Belgesi': s.documents_status?.language_cert,
            'Eksik Evrak': s.documents_status?.missing_documents,
            'Pasaport': s.documents_status?.passport_status,
            '1. Sınav Tarih': s.exam_data?.exam1_date,
            '1. Sınav Puan': s.exam_data?.exam1_score,
            '2. Sınav Tarih': s.exam_data?.exam2_date,
            '2. Sınav Puan': s.exam_data?.exam2_score,
            '3. Sınav Tarih': s.exam_data?.exam3_date,
            '3. Sınav Puan': s.exam_data?.exam3_score,
            'Sınav Ücreti': s.exam_data?.exam_fee_status,
            'Kayıt Ücreti': s.exam_data?.enrollment_fee_status,
            'Notlar': s.notes
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Mühendislik Öğrencileri');
        XLSX.writeFile(wb, 'Muhendislik_Ogrencileri.xlsx');
    };

    const renderCell = (student: EngineeringStudent, field: string, nestedField?: string, subField?: string) => {
        let value: any = student;
        if (nestedField) value = value[nestedField];
        if (subField) value = value?.[subField];
        else if (!nestedField) value = value[field];

        return (
            <input
                type="text"
                className="w-full text-[10px] p-1 border rounded bg-white focus:ring-1 focus:ring-blue-500 hover:bg-gray-50 transition-colors"
                value={value || ''}
                onChange={(e) => updateStudentState(student.id, field, e.target.value, nestedField, subField)}
            />
        );
    };

    if (loading) return <div className="p-4">Yükleniyor...</div>;

    const filteredStudents = students.filter(s =>
        `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="space-y-4 p-4">
            <div className="flex justify-between items-center">
                <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Öğrenci ara..."
                        className="w-full pl-10 pr-4 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg text-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button
                    onClick={exportToExcel}
                    className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                >
                    <Download className="w-4 h-4" />
                    <span>Excel'e Aktar</span>
                </button>
            </div>

            <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <table className="w-full text-left border-collapse min-w-[2200px]" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {columnOrder.map(key => (
                            <col key={key} style={{ width: columnWidths[key] }} />
                        ))}
                    </colgroup>
                    <thead className="bg-gray-50 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-300 uppercase sticky top-0 z-10">
                        <tr>
                            <th className="p-2 border dark:border-gray-600 relative group" rowSpan={2}>
                                Dil Belgesi
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'lang_cert')} />
                            </th>
                            <th className="p-2 border dark:border-gray-600 relative group" rowSpan={2}>
                                Eksik Evrak
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'missing_docs')} />
                            </th>
                            <th className="p-2 border dark:border-gray-600 relative group" rowSpan={2}>
                                Ad
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'first_name')} />
                            </th>
                            <th className="p-2 border dark:border-gray-600 relative group" rowSpan={2}>
                                Soyad
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'last_name')} />
                            </th>
                            <th className="p-2 border dark:border-gray-600 text-center" colSpan={2}>Tercihler</th>

                            <th className="p-2 border dark:border-gray-600 text-center bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200" colSpan={2}>
                                Universitaly
                            </th>

                            <th className="p-2 border dark:border-gray-600 text-center bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200" colSpan={3}>
                                Okul Portalı
                            </th>

                            <th className="p-2 border dark:border-gray-600 relative group text-center" rowSpan={2}>
                                Dil Skor
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'lang_score')} />
                            </th>
                            <th className="p-2 border dark:border-gray-600 relative group text-center" rowSpan={2}>
                                Pasaport
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'passport')} />
                            </th>

                            <th className="p-2 border dark:border-gray-600 text-center" colSpan={2}>1. Sınav</th>
                            <th className="p-2 border dark:border-gray-600 text-center" colSpan={2}>2. Sınav</th>
                            <th className="p-2 border dark:border-gray-600 text-center" colSpan={2}>3. Sınav</th>

                            <th className="p-2 border dark:border-gray-600 relative group text-center" rowSpan={2}>
                                Ücretler
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'fees')} />
                            </th>
                            <th className="p-2 border dark:border-gray-600 relative group text-center" rowSpan={2}>
                                Notlar
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'notes')} />
                            </th>
                            <th className="p-2 border dark:border-gray-600 relative group text-center" rowSpan={2}>
                                İşlem
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'actions')} />
                            </th>
                        </tr>
                        <tr>
                            <th className="p-1 border dark:border-gray-600 text-[10px] relative group">
                                Üniversite
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'university')} />
                            </th>
                            <th className="p-1 border dark:border-gray-600 text-[10px] relative group">
                                Bölüm
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'program')} />
                            </th>

                            <th className="p-1 border dark:border-gray-600 text-[10px] relative group">
                                Kullanıcı
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'uni_username')} />
                            </th>
                            <th className="p-1 border dark:border-gray-600 text-[10px] relative group">
                                Şifre
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'uni_password')} />
                            </th>

                            <th className="p-1 border dark:border-gray-600 text-[10px] relative group">
                                Kullanıcı
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'portal_username')} />
                            </th>
                            <th className="p-1 border dark:border-gray-600 text-[10px] relative group">
                                Şifre
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'portal_password')} />
                            </th>
                            <th className="p-1 border dark:border-gray-600 text-[10px] relative group">
                                SAT
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'sat_score')} />
                            </th>

                            <th className="p-1 border dark:border-gray-600 text-[10px] text-center relative group">
                                Tarih
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'exam1_date')} />
                            </th>
                            <th className="p-1 border dark:border-gray-600 text-[10px] text-center relative group">
                                Puan
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'exam1_score')} />
                            </th>
                            <th className="p-1 border dark:border-gray-600 text-[10px] text-center relative group">
                                Tarih
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'exam2_date')} />
                            </th>
                            <th className="p-1 border dark:border-gray-600 text-[10px] text-center relative group">
                                Puan
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'exam2_score')} />
                            </th>
                            <th className="p-1 border dark:border-gray-600 text-[10px] text-center relative group">
                                Tarih
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'exam3_date')} />
                            </th>
                            <th className="p-1 border dark:border-gray-600 text-[10px] text-center relative group">
                                Puan
                                <div className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" onMouseDown={(e) => handleMouseDown(e, 'exam3_score')} />
                            </th>
                        </tr>
                    </thead>
                    <tbody className="text-sm divide-y divide-gray-200 dark:divide-gray-700">
                        <tr className="bg-blue-50/50 dark:bg-blue-900/10 border-b border-blue-100 dark:border-blue-900/30">
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    placeholder="Dil"
                                    value={newStudent.documents_status.language_cert}
                                    onChange={e => setNewStudent({ ...newStudent, documents_status: { ...newStudent.documents_status, language_cert: e.target.value } })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    placeholder="Eksik"
                                    value={newStudent.documents_status.missing_documents}
                                    onChange={e => setNewStudent({ ...newStudent, documents_status: { ...newStudent.documents_status, missing_documents: e.target.value } })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    placeholder="Ad"
                                    className="w-full text-xs p-1 border rounded"
                                    value={newStudent.first_name}
                                    onChange={e => setNewStudent({ ...newStudent, first_name: e.target.value })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    placeholder="Soyad"
                                    className="w-full text-xs p-1 border rounded"
                                    value={newStudent.last_name}
                                    onChange={e => setNewStudent({ ...newStudent, last_name: e.target.value })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <select
                                    className="w-full text-[10px] p-1 border rounded"
                                    value={newStudent.university}
                                    onChange={e => setNewStudent({ ...newStudent, university: e.target.value })}
                                >
                                    {programType === 'all-engineering' ? (
                                        <>
                                            <option value="">Üniversite Seçin...</option>
                                            {availableUniversities.map(uni => (
                                                <option key={uni.id} value={uni.name}>{uni.name}</option>
                                            ))}
                                        </>
                                    ) : (
                                        <>
                                            <option value="">Üniversite Seçin...</option>
                                            <option value="Politecnico di Torino">Politecnico di Torino</option>
                                            <option value="Politecnico di Milano">Politecnico di Milano</option>
                                            {availableUniversities.filter(u => u.name !== 'Politecnico di Torino' && u.name !== 'Politecnico di Milano').map(uni => (
                                                <option key={uni.id} value={uni.name}>{uni.name}</option>
                                            ))}
                                        </>
                                    )}
                                </select>
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    placeholder="Bölüm"
                                    className="w-full text-[10px] p-1 border rounded"
                                    value={newStudent.program}
                                    onChange={e => setNewStudent({ ...newStudent, program: e.target.value })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    value={newStudent.universitaly_data.username}
                                    onChange={e => setNewStudent({ ...newStudent, universitaly_data: { ...newStudent.universitaly_data, username: e.target.value } })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    value={newStudent.universitaly_data.password}
                                    onChange={e => setNewStudent({ ...newStudent, universitaly_data: { ...newStudent.universitaly_data, password: e.target.value } })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    value={newStudent.school_portal_data.username}
                                    onChange={e => setNewStudent({ ...newStudent, school_portal_data: { ...newStudent.school_portal_data, username: e.target.value } })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    value={newStudent.school_portal_data.password}
                                    onChange={e => setNewStudent({ ...newStudent, school_portal_data: { ...newStudent.school_portal_data, password: e.target.value } })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    value={newStudent.school_portal_data.sat_score}
                                    onChange={e => setNewStudent({ ...newStudent, school_portal_data: { ...newStudent.school_portal_data, sat_score: e.target.value } })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    value={newStudent.documents_status.language_cert}
                                    onChange={e => setNewStudent({ ...newStudent, documents_status: { ...newStudent.documents_status, language_cert: e.target.value } })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    value={newStudent.documents_status.passport_status}
                                    onChange={e => setNewStudent({ ...newStudent, documents_status: { ...newStudent.documents_status, passport_status: e.target.value } })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    value={newStudent.exam_data.exam1_date}
                                    onChange={e => setNewStudent({ ...newStudent, exam_data: { ...newStudent.exam_data, exam1_date: e.target.value } })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    value={newStudent.exam_data.exam1_score}
                                    onChange={e => setNewStudent({ ...newStudent, exam_data: { ...newStudent.exam_data, exam1_score: e.target.value } })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    value={newStudent.exam_data.exam2_date}
                                    onChange={e => setNewStudent({ ...newStudent, exam_data: { ...newStudent.exam_data, exam2_date: e.target.value } })}
                                />
                            </td>
                            <td className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    value={newStudent.exam_data.exam2_score}
                                    onChange={e => setNewStudent({ ...newStudent, exam_data: { ...newStudent.exam_data, exam2_score: e.target.value } })}
                                />
                            </td>
                            <td style={{ width: columnWidths.exam3_date }} className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    value={newStudent.exam_data.exam3_date}
                                    onChange={e => setNewStudent({ ...newStudent, exam_data: { ...newStudent.exam_data, exam3_date: e.target.value } })}
                                />
                            </td>
                            <td style={{ width: columnWidths.exam3_score }} className="p-2 border dark:border-gray-700">
                                <input
                                    className="w-full text-[10px] p-1 border rounded bg-white"
                                    value={newStudent.exam_data.exam3_score}
                                    onChange={e => setNewStudent({ ...newStudent, exam_data: { ...newStudent.exam_data, exam3_score: e.target.value } })}
                                />
                            </td>
                            <td style={{ width: columnWidths.fees }} className="p-2 border dark:border-gray-700">
                                <div className="space-y-1">
                                    <input
                                        className="w-full text-[10px] p-1 border rounded bg-white"
                                        placeholder="Sınav"
                                        value={newStudent.exam_data.exam_fee_status}
                                        onChange={e => setNewStudent({ ...newStudent, exam_data: { ...newStudent.exam_data, exam_fee_status: e.target.value } })}
                                    />
                                    <input
                                        className="w-full text-[10px] p-1 border rounded bg-white"
                                        placeholder="Kayıt"
                                        value={newStudent.exam_data.enrollment_fee_status}
                                        onChange={e => setNewStudent({ ...newStudent, exam_data: { ...newStudent.exam_data, enrollment_fee_status: e.target.value } })}
                                    />
                                </div>
                            </td>
                            <td style={{ width: columnWidths.notes }} className="p-2 border dark:border-gray-700">
                                <textarea
                                    className="w-full text-[10px] p-1 border rounded bg-white h-7 resize-none"
                                    placeholder="Notlar"
                                    value={newStudent.notes}
                                    onChange={e => setNewStudent({ ...newStudent, notes: e.target.value })}
                                />
                            </td>
                            <td style={{ width: columnWidths.actions }} className="p-2 border dark:border-gray-700 text-center">
                                <button
                                    onClick={handleAddStudent}
                                    disabled={isAdding || !newStudent.first_name.trim() || !newStudent.last_name.trim()}
                                    className="p-1 px-2 border dark:border-gray-600 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded text-[10px] font-bold transition-colors uppercase"
                                >
                                    {isAdding ? '...' : 'Kaydet'}
                                </button>
                            </td>
                        </tr>
                        {filteredStudents.map(student => (
                            <tr key={student.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <td style={{ width: columnWidths.lang_cert }} className="p-2 border dark:border-gray-700">
                                    {renderCell(student, '', 'documents_status', 'language_cert')}
                                </td>
                                <td style={{ width: columnWidths.missing_docs }} className="p-2 border dark:border-gray-700">
                                    {renderCell(student, '', 'documents_status', 'missing_documents')}
                                </td>
                                <td style={{ width: columnWidths.first_name }} className="p-2 border dark:border-gray-700">
                                    <input
                                        className="w-full text-[10px] p-1 border rounded bg-white"
                                        value={student.first_name}
                                        onChange={(e) => updateStudentState(student.id, 'first_name', e.target.value)}
                                    />
                                </td>
                                <td style={{ width: columnWidths.last_name }} className="p-2 border dark:border-gray-700">
                                    <input
                                        className="w-full text-[10px] p-1 border rounded bg-white"
                                        value={student.last_name}
                                        onChange={(e) => updateStudentState(student.id, 'last_name', e.target.value)}
                                    />
                                </td>
                                <td style={{ width: columnWidths.university }} className="p-2 border dark:border-gray-700">
                                    {renderCell(student, '', 'universitaly_data', 'university')}
                                </td>
                                <td style={{ width: columnWidths.program }} className="p-2 border dark:border-gray-700">
                                    {renderCell(student, '', 'universitaly_data', 'program')}
                                </td>
                                <td style={{ width: columnWidths.uni_username }} className="p-2 border dark:border-gray-700 bg-blue-50/30">
                                    {renderCell(student, '', 'universitaly_data', 'username')}
                                </td>
                                <td style={{ width: columnWidths.uni_password }} className="p-2 border dark:border-gray-700 bg-blue-50/30">
                                    {renderCell(student, '', 'universitaly_data', 'password')}
                                </td>
                                <td style={{ width: columnWidths.portal_username }} className="p-2 border dark:border-gray-700 bg-green-50/30">
                                    {renderCell(student, '', 'school_portal_data', 'username')}
                                </td>
                                <td style={{ width: columnWidths.portal_password }} className="p-2 border dark:border-gray-700 bg-green-50/30">
                                    {renderCell(student, '', 'school_portal_data', 'password')}
                                </td>
                                <td style={{ width: columnWidths.sat_score }} className="p-2 border dark:border-gray-700 bg-green-50/30">
                                    {renderCell(student, '', 'school_portal_data', 'sat_score')}
                                </td>
                                <td style={{ width: columnWidths.lang_score }} className="p-2 border dark:border-gray-700">
                                    {renderCell(student, '', 'documents_status', 'language_cert')}
                                </td>
                                <td style={{ width: columnWidths.passport }} className="p-2 border dark:border-gray-700">
                                    {renderCell(student, '', 'documents_status', 'passport_status')}
                                </td>

                                {/* Exam 1 */}
                                <td style={{ width: columnWidths.exam1_date }} className="p-2 border dark:border-gray-700">
                                    {renderCell(student, '', 'exam_data', 'exam1_date')}
                                </td>
                                <td style={{ width: columnWidths.exam1_score }} className="p-2 border dark:border-gray-700">
                                    {renderCell(student, '', 'exam_data', 'exam1_score')}
                                </td>

                                {/* Exam 2 */}
                                <td style={{ width: columnWidths.exam2_date }} className="p-2 border dark:border-gray-700">
                                    {renderCell(student, '', 'exam_data', 'exam2_date')}
                                </td>
                                <td style={{ width: columnWidths.exam2_score }} className="p-2 border dark:border-gray-700">
                                    {renderCell(student, '', 'exam_data', 'exam2_score')}
                                </td>

                                {/* Exam 3 */}
                                <td style={{ width: columnWidths.exam3_date }} className="p-2 border dark:border-gray-700">
                                    {renderCell(student, '', 'exam_data', 'exam3_date')}
                                </td>
                                <td style={{ width: columnWidths.exam3_score }} className="p-2 border dark:border-gray-700">
                                    {renderCell(student, '', 'exam_data', 'exam3_score')}
                                </td>

                                <td style={{ width: columnWidths.fees }} className="p-2 border dark:border-gray-700">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-1">
                                            <span className="text-[9px] w-8">Sınav:</span>
                                            {renderCell(student, '', 'exam_data', 'exam_fee_status')}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <span className="text-[9px] w-8">Kayıt:</span>
                                            {renderCell(student, '', 'exam_data', 'enrollment_fee_status')}
                                        </div>
                                    </div>
                                </td>
                                <td style={{ width: columnWidths.notes }} className="p-2 border dark:border-gray-700">
                                    {renderCell(student, 'notes')}
                                </td>
                                <td style={{ width: columnWidths.actions }} className="p-2 border dark:border-gray-700 text-center">
                                    <div className="flex items-center justify-center gap-2">
                                        <button
                                            onClick={() => handleSave(student)}
                                            className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                                            title="Kaydet"
                                        >
                                            <Save size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(student.id)}
                                            className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                            title="Sil"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {filteredStudents.length === 0 && (
                            <tr>
                                <td colSpan={22} className="p-4 text-center text-gray-500">Kayıt bulunamadı.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default EngineeringTable;
